import { FC, StrictMode, useEffect, useMemo } from 'react';

import { HashBrowserRouter } from '@servicetitan/hash-browser-router';
import { EVENT_BUS_TOKEN, EventBus, Loader } from '@servicetitan/web-components';

import './setup-logger';

import { Spinner, Stack } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import axios from 'axios';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import { MFE_POINTER_CLASS_NAME } from 'utils';
import './app.less';
import { FeBundleApi } from './common/api/fe-bundle.api';
import { ClientContext, ClientContextApi } from './common/api/client-context.api';
import { FeBundleStore } from './common/stores/fe-bundle.store';
import { reportHostError } from './datadog';

axios.defaults.headers.common['X-CSRF'] = '1';

interface AppData {
    jobId?: string | null;
    proposalId?: string | null;
    step?: number | null;
    subStep?: number | null;
    clientContext?: ClientContext | null;
}

export const HostApp: FC = provide({
    singletons: [FeBundleApi, ClientContextApi, FeBundleStore],
})(
    observer(() => {
        const [{ init, bundleUrl, isError, clientContext }] = useDependencies(FeBundleStore);

        useEffect(() => {
            const isPilot = window.location.hash.includes('isPilot=1');
            const isLegacy = window.location.hash.includes('isLegacy=1');
            init(isPilot, isLegacy);
        }, [init]);

        return (
            <HashBrowserRouter>
                <StrictMode>
                    <Stack direction="column" className="h-100vh">
                        {bundleUrl && (
                            <CustomizedLoader bundleUrl={bundleUrl} clientContext={clientContext} />
                        )}
                        {isError && <div>Error loading the bundle.</div>}
                    </Stack>
                </StrictMode>
            </HashBrowserRouter>
        );
    }),
);

HostApp.displayName = 'HostApp';

interface CustomizedLoaderProps {
    bundleUrl: string;
    clientContext: ClientContext | null;
}

const CustomizedLoader = provide({
    singletons: [{ provide: EVENT_BUS_TOKEN, useValue: new EventBus() }],
})((props: CustomizedLoaderProps) => {
    const { bundleUrl, clientContext } = props;
    const { search } = useLocation();

    const data: AppData = useMemo(() => {
        const params = new URLSearchParams(search);
        const proposalId = params.get('contractProposalId');
        const jobId = params.get('jobId');
        const step = params.get('step');
        const subStep = params.get('subStep');
        return {
            proposalId: proposalId ?? undefined,
            jobId: jobId ?? undefined,
            step: step !== null ? Number(step) : undefined,
            subStep: subStep !== null ? Number(subStep) : undefined,
            clientContext,
        };
    }, [search, clientContext]);

    return (
        <Loader
            className={MFE_POINTER_CLASS_NAME}
            loadingFallback={
                <Stack className="w-100 h-100" justifyContent="center" alignItems="center">
                    <Stack.Item>
                        <Spinner size="large" />
                    </Stack.Item>
                </Stack>
            }
            src={bundleUrl}
            data={data}
            onRequestError={error => {
                reportHostError('cpb-host: bundle load failed', {
                    bundleUrl,
                    status: error.status,
                    body: error.body.slice(0, 500),
                });
                return false;
            }}
        />
    );
});
